import * as React from "react";
import { Col, Form, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import {
  AgencyInfo,
  CountyInfo,
  ErrorInfo,
  initialKeywordValues,
  ReportTypesInfo
} from "../business-objects/common";
import "../assets/keywordsform.scss";
import RBSelectList from "../../../RBSelectList/RBSelectList";
import { IInvolvedPartyProps, InvolvedParty } from "./InvolvedParty";
import { IReportTypeProps } from "./ReportType";
import Select from "react-select";
import * as optionIds from "../../../shared/optionIds";
import { IKeywords } from "../../../../common/sharedTypes";
import dateFnsFormat from "date-fns/format";
import "react-datepicker/dist/react-datepicker.css";
import NotificationTemplateSelector from "../../../NotificationTemplate/NotificationTemplateSelector";
import CommentsButton from "../../../Comments/CommentsButton";
import { PdfViewer } from "../../../shared/PdfViewer/PdfViewer";
import Input from "../../../Elements/Input";

const moment = require("moment");

interface IKeywordsFormStateProps {
  keywords: IKeywords;
  agencies: AgencyInfo[];
  counties: CountyInfo[];
  reportTypes: ReportTypesInfo[];
  errors: ErrorInfo;
  showNotificationTemplateSelector?: boolean;
  reportId?: number;
  image?: Uint8Array;
  splitCaseMode?: boolean;
  canEditReportType?: boolean;
}

interface IKeywordsFormActions {
  onInvolvedPartyChange: (event: any) => void;
  onInvolvedPartyBlur: (event: any) => void;
  onChange: (event: any) => void;
  onDateChange: (date: any) => void;
}

export interface IKeywordsFormProps
  extends IKeywordsFormStateProps,
  IKeywordsFormActions { }

export const injuryStatusOptions = [
  { name: "Fatality", value: "FATAL" },
  { name: "Injury-A", value: "INJURY-A" },
  { name: "Other Injury", value: "INJURY" },
  { name: "PDO", value: "PDO" },
];

export const truckBusOptions = [
  { id: 1, name: "Yes" },
  { id: 2, name: "No" }
];

export const yesNo = [
  { id: 1, description: "Yes" },
  { id: 2, description: "No" }
];

export const KeywordsForm: React.FC<IKeywordsFormProps> = ({
  keywords,
  agencies,
  counties,
  reportTypes,
  showNotificationTemplateSelector,
  onInvolvedPartyChange,
  onInvolvedPartyBlur,
  onDateChange,
  onChange,
  errors,
  reportId,
  image,
  splitCaseMode,
  canEditReportType
}) => {
  const keywordsToUse = keywords !== null ? keywords : initialKeywordValues;

  const reportTypeProps: IReportTypeProps = {
    reportTypes,
    value: keywords.reportTypeId,
    onChange: onChange
  };

  let dateCompare = new Date("2021-01-01");
  let reportDate = new Date(keywords.dateOfCrash);
  const DateOfCrashDatePicker = (
    <Form.Group controlId="DateOfCrash">
      <Form.Label>Date Of Crash</Form.Label>
      <br />
      <DatePicker
        selected={
          keywords.dateOfCrash ? Date.parse(keywords.dateOfCrash as string) : ""
        }
        onChange={(date) => onDateChange(date)}
        isClearable
        placeholderText="Select Date"
        tabIndex="1"
      />
      {errors.crashDate !== "" && (
        <Form.Label className="error">{errors.crashDate}</Form.Label>
      )}
    </Form.Group>
  );

  const onCountyChange = ({ value }) => {
    const event = {
      persist: () => { },
      target: {
        type: "manual",
        id: "0" + value,
        name: "countyId",
        value: value
      }
    };
    onChange(event);
  };

  const county =
    keywords.countyId && keywords.countyId != -1
      ? counties.find((county) => county.id == keywords.countyId)
      : null;
  const countyDescription = county ? county.description : "";
  const countyId = county ? county.name : -1;
  const countyOption =
    countyDescription != "" || countyId != -1
      ? {
        value: countyId,
        label: countyId + " - " + countyDescription
      }
      : "";

  const countyOptions = counties
    .filter(c => c.name !== '00') // filter out unknown
    .map(c => ({
      label: `${c.name} - ${c.description}`,
      value: c.id,
    }));

  const countiesDropDown = (
    <Form.Group className="selectlist">
      <Form.Label>County</Form.Label>
      <Select
        placeholder={"Select a county"}
        options={countyOptions}
        onChange={onCountyChange}
        value={countyOption}
        tabIndex="2"
      />
      {errors.countySelection !== "" && (
        <Form.Label className="error">{errors.countySelection}</Form.Label>
      )}
    </Form.Group>
  );

  //const reportTypesGroup = <ReportType {...reportTypeProps} />;

  const reportTypesDropdown = (
    <Form.Group className="selectlist">
      <Form.Label>Report Type</Form.Label>
      <RBSelectList
        title="Report Type"
        name="reportTypeId"
        placeholder="Select a Report Type"
        className="w-100"
        datafield=""
        data={
          reportTypes &&
          reportTypes.length > 0 &&
          reportTypes.map((reportType) => ({
            name: reportType.name,
            value: reportType.id
          }))
        }
        value={keywords.reportTypeId && keywords.reportTypeId.toString()}
        onChange={onChange}
        tabindex={3}
        disabled={canEditReportType === undefined ? false : !canEditReportType}
      />
    </Form.Group>
  );

  const onAgencyChange = (value, action) => {
    if (action.action != "clear") {
      const event = {
        persist: () => { },
        target: {
          type: "manual",
          id: value,
          name: "agencyId",
          value: value.value
        }
      };
      onChange(event);
    } else if (action.action == "clear") {
      const event = {
        persist: () => { },
        target: {
          type: "manual",
          id: value,
          name: "agencyId",
          value: null
        }
      };
      onChange(event);
    }
  };

  const handleTab = reportTypeProps.value != 4 ? -1 : 4;

  const agencyList = agencies
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((agency) => ({
      label: agency.name,
      value: agency.id
    }));

  const filterNarrativeInput = (e) => {
    //console.debug(e)
    if ((e.which >= 48 && e.which <= 57) // top row numbers
      || e.which == 8  // backspace
      || e.which == 46 // delete 
      || e.which == 188 // comma
      || (e.which >= 96 && e.which <= 105)) // numpad 
    {
      // ok        
    } else {
      // not ok input
      e.preventDefault();
      return false;
    }
  }

  const AgencyDropDown = (
    <Form.Group className="selectlist">
      <Form.Label>Agency</Form.Label>
      <Select
        placeholder={"Select an Agency"}
        options={agencyList}
        onChange={(value, action) => {
          onAgencyChange(value, action);
        }}
        value={agencyList.filter((x) => x.value == keywords.agencyId)[0] || ""}
        //value={keywords.agencyId || ''}
        isClearable={true}
        //onKeyDown={handleKeyDown}
        tabIndex={handleTab}
      />
      {errors.agencySelection !== "" && (
        <Form.Label className="error">{errors.agencySelection}</Form.Label>
      )}
    </Form.Group>
  );

  const NonMotoristDropDown = (
    <Form.Group className="selectlist">
      <Form.Label>Non-Motorist</Form.Label>
      <RBSelectList
        title="Non-Motorist"
        name="nonMotoristIndicator"
        placeholder="Select non-motorist "
        className="w-100"
        datafield=""
        data={yesNo.map((item) => ({
          id: item.id,
          name: item.description
        }))}
        value={keywordsToUse.nonMotoristIndicator || ""}
        onChange={onChange}
        tabindex={9}
      />
    </Form.Group>
  );

  const InjuryStatusDropdown = (
    <Form.Group className="selectlist">
      <Form.Label>Injury Status</Form.Label>
      <RBSelectList
        title="Injury Status"
        name="injuryStatus"
        placeholder="Select an Injury Status"
        className="w-100"
        datafield=""
        data={injuryStatusOptions}
        value={keywordsToUse.injuryStatus || ""}
        onChange={onChange}
        tabindex={7}
      />
      {errors.injurySelection !== "" && (
        <Form.Label className="error">{errors.injurySelection}</Form.Label>
      )}
    </Form.Group>
  );

  const workZoneDropDown = (
    <Form.Group className="selectlist">
      <Form.Label>Workzone</Form.Label>
      <RBSelectList
        title="WorkZone "
        name="workZoneIndicator"
        placeholder="Select workzone "
        className="w-100"
        datafield=""
        data={yesNo.map((item) => ({
          id: item.id,
          name: item.description
        }))}
        value={keywordsToUse.workZoneIndicator || ""}
        onChange={onChange}
        tabindex={6}
      />
    </Form.Group>
  );

  const statePropertyDamageDropDown = (
    <Form.Group className="selectlist">
      <Form.Label>State Property Damaged (SPD)</Form.Label>
      <RBSelectList
        title="State Property Damaged (SPD)"
        name="propertyDamageIndicator"
        placeholder="Select State Property Damaged (SPD)"
        className="w-100"
        datafield=""
        data={yesNo.map((item) => ({
          id: item.id,
          name: item.description
        }))}
        value={keywordsToUse.propertyDamageIndicator || ""}
        onChange={onChange}
        tabindex={5}
      />
    </Form.Group>
  );

  const truckBusDropDown = (
    <Form.Group className="selectlist">
      <Form.Label>Truck / Bus</Form.Label>
      <RBSelectList
        title="Truck / Bus"
        name="truckBusIndicator"
        placeholder="Select Truck/Bus "
        className="w-100"
        datafield=""
        data={truckBusOptions}
        value={keywordsToUse.truckBusIndicator || ""}
        onChange={onChange}
        tabindex={8}
      />
    </Form.Group>
  );

  const involvedPartyCollection = (
    <Form.Group>
      <InvolvedParty
        involvedParties={keywords.involvedParties}
        onChange={onInvolvedPartyChange}
        onIpBlur={onInvolvedPartyBlur}
        errorLength={errors.involvedPartyLength}
        errorFirstName={errors.involvedPartyFirstName}
        errorLastName={errors.involvedPartyLastName}
        errorBusinessName={errors.involvedPartyBusinessName}
      />
    </Form.Group>
  );

  const imagePreview = (
    <React.Fragment>
      <Form.Group>
        <Form.Label>Narrative / Diagram pages</Form.Label>

        {keywordsToUse.narrativeDiagramPages &&
          keywordsToUse.narrativeDiagramPages.length > 0 && (
            <div className="divImagePreview">
              {keywordsToUse.narrativeDiagramPages
                .replace(" ", "")
                .split(",")
                .map((x, index) => {
                  if (parseInt(x)) {
                    return (
                      <PdfViewer
                        key={`page${x}-${index}`}
                        image={image}
                        pageWidth={200}
                        hideNavigation={true}
                        showPage={x}
                      />
                    );
                  }
                })}
            </div>
          )}
        {(!keywordsToUse.narrativeDiagramPages ||
          keywordsToUse.narrativeDiagramPages.length < 1) && (
            <div className="divImagePreviewEmpty"></div>
          )}
        <Input
          title=""
          type="text"
          name="narrativeDiagramPages"
          value={keywordsToUse.narrativeDiagramPages}
          onChange={onChange}
          onKeyDown={filterNarrativeInput}
          disabled={keywordsToUse.reportTypeId != 4}
          placeholder={"ex: 1,2"}
          tabIndex="10"
        />
        {errors.narrativeDiagram !== "" && (
          <Form.Label className="error">{errors.narrativeDiagram}</Form.Label>
        )}
      </Form.Group>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      {!splitCaseMode && (
        <React.Fragment>
          <Row>
            <Col>
              <h2>Keyword Entry</h2>
            </Col>
            <Col className="justify-content-end">
              {" "}
              <NotificationTemplateSelector
                optionId={optionIds.keywordsOptionId}
                reportId={reportId}
              />
            </Col>
            <Col xs={1} className="justify-content-end">
              <CommentsButton
                reportId={reportId}
                className="pull-right"
                placement="left"
              />
            </Col>
          </Row>
          <Row></Row>
        </React.Fragment>
      )}
      <Form className="container keywordsform">
        <div
          style={{
            display:
              showNotificationTemplateSelector != null &&
                showNotificationTemplateSelector != undefined &&
                !!!showNotificationTemplateSelector
                ? "none"
                : "true"
          }}
        >
          {" "}
        </div>

        <Row>
          <Col>
            <Form.Group as={Row} controlId="formDatePicker">
              <Col>{DateOfCrashDatePicker}</Col>
            </Form.Group>
            <Row>
              <Col xs={9}>
                <Form.Group as={Row} controlId="countyAndReportType">
                  <Col>{countiesDropDown}</Col>
                  <Col>{reportTypesDropdown}</Col>
                  <Col>{AgencyDropDown}</Col>
                </Form.Group>
                <Form.Group as={Row} controlId="agencyInjuryAndWorkZone">
                  <Col xs="2"></Col>
                  <Col>{statePropertyDamageDropDown}</Col>
                  <Col>{workZoneDropDown}</Col>
                </Form.Group>
                <Form.Group
                  as={Row}
                  controlId="privatePropertyPropertyDamageTruckBus"
                >
                  <Col xs="2"></Col>
                  <Col>{InjuryStatusDropdown}</Col>
                  <Col>{truckBusDropDown}</Col>
                  <Col>{NonMotoristDropDown}</Col>
                </Form.Group>
                <Form.Group as={Row}>
                  <Col>{involvedPartyCollection}</Col>
                </Form.Group>
              </Col>
              <Col xs={3}>{imagePreview}</Col>
            </Row>
          </Col>
        </Row>
        <Row>{"  "}</Row>
      </Form>
    </React.Fragment>
  );
};

export default KeywordsForm;
